import React, { useState } from 'react';
import { Phone, MessageSquare, Mail } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend
      await axios.post(`${API}/lead`, formData);
      
      toast({
        title: "Quote Request Sent!",
        description: "We've received your request and will contact you shortly at " + formData.phone,
      });
      
      // Reset form
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Request Received",
        description: "We'll get back to you soon at " + formData.phone,
      });
      // Reset form anyway
      setFormData({ name: '', phone: '', email: '', message: '' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 scroll-animate">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Request a <span className="text-red-500">Quote</span>
          </h2>
          <p className="text-xl text-gray-300">
            Tell us what you need and we'll respond quickly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card className="scroll-animate-left bg-black/50 border-red-900/30">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">Name *</label>
                  <Input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-gray-800 border-gray-700 text-white focus:border-red-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Phone *</label>
                  <Input 
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-gray-800 border-gray-700 text-white focus:border-red-500"
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Email</label>
                  <Input 
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-gray-800 border-gray-700 text-white focus:border-red-500"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">What do you need help with? *</label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-gray-800 border-gray-700 text-white focus:border-red-500"
                    placeholder="Describe your project or service needed..."
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg transition-all duration-300"
                >
                  {isSubmitting ? 'Sending...' : 'Send Quote Request'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Fast Contact */}
          <div className="space-y-6 scroll-animate-right">
            <Card className="bg-gradient-to-br from-red-900/30 to-black border-2 border-red-500">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Fast Contact
                </h3>
                <p className="text-gray-300 mb-6">
                  Need immediate help? Give us a call, text, or email!
                </p>
                <div className="space-y-4">
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg"
                    onClick={() => window.location.href = 'tel:8134491138'}
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: (813) 449-1138
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-2 border-white text-white hover:bg-white hover:text-black font-semibold py-6 text-lg"
                    onClick={() => window.location.href = 'sms:8134491138'}
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Text Us: (813) 449-1138
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-2 border-white text-white hover:bg-white hover:text-black font-semibold py-6 text-lg"
                    onClick={() => window.location.href = 'mailto:info@swifthandymen.com'}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    info@swifthandymen.com
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Service Image */}
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1676311396794-f14881e9daaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxoYW5keW1hbnxlbnwwfHx8fDE3NzIyMzA3OTB8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional tools"
                className="rounded-lg shadow-2xl shadow-red-600/20 w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
